import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import TabButton from "../../Components/common/TabButton/TabButton";
import ReactTableWrapper from "../../Components/common/ReactTableWrapper/ReactTableWrapper";
import SandboxVisitButton from "../../Components/common/SandboxVisit/SandboxVisitButton";
import SandboxVisitConfirm from "../../Components/common/SandboxVisit/SandboxVisitConfirm";
import {
  DetailActions,
  DetailField,
  DetailGrid,
  DetailPanel,
} from "../../Components/common/DetailPanel/DetailPanel";

import axiosApi from "../../utils/axios";
import { URL_REGEX } from "../../utils/constants/regex";
import { dynamicPhishingHeader } from "../../utils/constants/tableHeaders";

import {
  RequestDynamicPhishingEmails,
  SetDynamicPhishingActiveTab,
} from "../../store/actions/phishBoxActions";
import { addNotification } from "../../store/actions/notifierActions";

import "./styles.scss";

const PAGE_SIZE = 10;
const TABLE_NAME = "dynamicPhishingEmails";

const Detail = ({ row, dispatch }) => {
  const description = row?.original?._source?.description || "-";
  const isUnAttended = description.includes("controlled");
  const url = row?.original?._source?.target_url;
  const [showVisitConfirm, setShowVisitConfirm] = useState(false);

  // An invalid URL is reported straight away; a valid one is only opened
  // after the user confirms it.
  const requestVisitHandler = () => {
    if (!URL_REGEX.test(url)) {
      dispatch(
        addNotification({
          message: "Not a valid url",
          options: { variant: "error" },
        }),
      );
      return;
    }

    setShowVisitConfirm(true);
  };

  const visitHandler = async () => {
    try {
      const response = await axiosApi.get(
        `/api/phishbox/url-rewrite?url=${encodeURIComponent(url)}`,
      );
      const rewritedUrl = response?.data?.url;
      if (rewritedUrl) {
        window.open(`/psh1/view?u=${rewritedUrl}`, "_blank");
      } else {
        dispatch(
          addNotification({
            message: "Failed to visit page",
            options: { variant: "error" },
          }),
        );
      }
    } catch (e) {
      console.log(e);
      dispatch(
        addNotification({
          message: "Unexpected error",
          options: { variant: "error" },
        }),
      );
    }
  };

  return (
    <DetailPanel>
      <DetailGrid columns={1}>
        <DetailField label="Link" mono>
          {url}
        </DetailField>
        <DetailField label="Analysis">{description}</DetailField>
      </DetailGrid>
      {isUnAttended ? (
        <DetailActions>
          <SandboxVisitButton onClick={requestVisitHandler} />
        </DetailActions>
      ) : null}
      <SandboxVisitConfirm
        url={url}
        show={showVisitConfirm}
        setShow={setShowVisitConfirm}
        onConfirm={visitHandler}
      />
    </DetailPanel>
  );
};

const DynamicPhishingEmails = () => {
  const dispatch = useDispatch();

  const dynamicPhishingEmails = useSelector((state) => state.phishBox.dynamicPhishingEmails);
  const loading = useSelector((state) => state.phishBox.dynamicPhishingEmailsLoading);
  const activeTab = useSelector((state) => state.phishBox.dynamicPhishingActiveTab);
  const isTabSwitching = useSelector((state) => state.phishBox.dynamicPhishingTabSwitching);

  useEffect(() => {
    dispatch(RequestDynamicPhishingEmails());
  }, [dispatch, activeTab]);

  const refreshHandler = () => dispatch(RequestDynamicPhishingEmails());

  return (
    <div className="dynamic-phishing-emails">
      <div className="dynamic-phishing-emails__header">
        <h1>PhishBoxed Links in Emails</h1>
        <p>Below list is displaying links which clicked via PhishBox in Emails:</p>
      </div>
      <div className="dynamic-phishing-emails__table border">
        <div className="button-block">
          <TabButton
            title="Latest"
            onClick={() => dispatch(SetDynamicPhishingActiveTab("Latest"))}
            active={activeTab}
          />
          <TabButton
            title="Not Allowed"
            onClick={() => dispatch(SetDynamicPhishingActiveTab("Not Allowed"))}
            active={activeTab}
          />
          <TabButton
            title="Visited"
            onClick={() => dispatch(SetDynamicPhishingActiveTab("Visited"))}
            active={activeTab}
          />
        </div>
        <ReactTableWrapper
          data={dynamicPhishingEmails}
          isDataLoading={loading}
          tableName={TABLE_NAME}
          columns={dynamicPhishingHeader}
          paginationButtons
          customPageSize={PAGE_SIZE}
          refreshConfig={{ show: true, onClickHandler: refreshHandler }}
          styles={{ table: isTabSwitching ? { opacity: "0.4" } : {} }}
          expandConfig={{
            use: true,
            expandOnRowClick: true,
            subComponent: Detail,
            syncToggle: true,
          }}
        />
      </div>
    </div>
  );
};

export default DynamicPhishingEmails;
